/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving depot batches.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialDepotBatchesState {
    depotBatches: Dtos.DepotBatch[] | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
};

export interface IDepotBatchesState {
    depotBatches: IInitialDepotBatchesState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const DepotBatchesInitialState: IInitialDepotBatchesState = {
    depotBatches: null,
    loadState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useDepotBatchesSelector: TypedUseSelectorHook<IDepotBatchesState> = useSelector;

export const DepotBatchesSelectors = {
    selectDepotBatches: (state: IDepotBatchesState) => state.depotBatches.depotBatches,
    selectLoadState: (state: IDepotBatchesState) => state.depotBatches.loadState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class DepotBatchesReducer extends ImmerReducer<IInitialDepotBatchesState> {
    public load() {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadByDepotId(depotId: number) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadByPharmacyId(pharmacyId: number) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadByInstCode(instCode: string) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadByDrugShipmentId(drugShipmentId: number) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(depotBatches?: Dtos.DepotBatch[]) {
        this.draftState.depotBatches = depotBatches ? depotBatches : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.depotBatches = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...DepotBatchesInitialState };
    }
};

export const DepotBatchesActions = createActionCreators(DepotBatchesReducer);
const reducer = createReducerFunction(DepotBatchesReducer, DepotBatchesInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const DepotBatchesApi = {
    load: () => {
        return client.get(new Dtos.GetDepotBatches());
    },
    loadByDepotId: (depotId: number) => {
        return client.get(new Dtos.GetDepotBatchesByDepotId({
            depotId
        }));
    },
    loadByPharmacyId: (pharmacyId: number) => {
        return client.get(new Dtos.GetDepotBatchesByPharmacyId({
            pharmacyId
        }));
    },
    loadByInstCode: (instCode: string) => {
        return client.get(new Dtos.GetDepotBatchesByInstitutionCode({
            instCode
        }));
    },
    loadByDrugShipmentId: (drugShipmentId: number) => {
        return client.get(new Dtos.GetDepotBatchesByDrugShipmentId({
            drugShipmentId
        }));
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const DepotBatchesLogic = {
    load: createLogic<IDepotBatchesState, {}, undefined, string, ReturnType<typeof DepotBatchesActions.load>>({
        type: DepotBatchesActions.load.type,
        process: async ({ action }, dispatch, done) => {
            try {
                const response = await DepotBatchesApi.load();

                dispatch(DepotBatchesActions.loadSuccess(
                    response.depotBatches
                ));
            }
            catch (error: any) {
                dispatch(DepotBatchesActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    loadByDepotId: createLogic<IDepotBatchesState, {}, undefined, string, ReturnType<typeof DepotBatchesActions.loadByDepotId>>({
        type: DepotBatchesActions.loadByDepotId.type,
        process: async ({ action }, dispatch, done) => {
            const depotId = action.payload;

            try {
                const response = await DepotBatchesApi.loadByDepotId(depotId);

                dispatch(DepotBatchesActions.loadSuccess(
                    response.depotBatches
                ));
            }
            catch (error: any) {
                dispatch(DepotBatchesActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    loadByPharmacyId: createLogic<IDepotBatchesState, {}, undefined, string, ReturnType<typeof DepotBatchesActions.loadByPharmacyId>>({
        type: DepotBatchesActions.loadByPharmacyId.type,
        process: async ({ action }, dispatch, done) => {
            const pharmacyId = action.payload;

            try {
                const response = await DepotBatchesApi.loadByPharmacyId(pharmacyId);

                dispatch(DepotBatchesActions.loadSuccess(
                    response.depotBatches
                ));
            }
            catch (error: any) {
                dispatch(DepotBatchesActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    loadByInstCode: createLogic<IDepotBatchesState, {}, undefined, string, ReturnType<typeof DepotBatchesActions.loadByInstCode>>({
        type: DepotBatchesActions.loadByInstCode.type,
        process: async ({ action }, dispatch, done) => {
            const instCode = action.payload;

            try {
                const response = await DepotBatchesApi.loadByInstCode(instCode);

                dispatch(DepotBatchesActions.loadSuccess(
                    response.depotBatches
                ));
            }
            catch (error: any) {
                dispatch(DepotBatchesActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    loadByDrugShipmentId: createLogic<IDepotBatchesState, {}, undefined, string, ReturnType<typeof DepotBatchesActions.loadByDrugShipmentId>>({
        type: DepotBatchesActions.loadByDrugShipmentId.type,
        process: async ({ action }, dispatch, done) => {
            const drugShipmentId = action.payload;

            try {
                const response = await DepotBatchesApi.loadByDrugShipmentId(drugShipmentId);

                dispatch(DepotBatchesActions.loadSuccess(
                    response.depotBatches
                ));
            }
            catch (error: any) {
                dispatch(DepotBatchesActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    DepotBatchesLogic.load,
    DepotBatchesLogic.loadByDepotId,
    DepotBatchesLogic.loadByPharmacyId,
    DepotBatchesLogic.loadByInstCode,
    DepotBatchesLogic.loadByDrugShipmentId
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const useDepotBatches = (): [Dtos.DepotBatch[] | null, IRequestState<Dtos.ResponseStatus>, typeof DepotBatchesActions] => {
    const depotBatches = useSelector(DepotBatchesSelectors.selectDepotBatches);
    const loadState = useSelector(DepotBatchesSelectors.selectLoadState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof DepotBatchesActions>(() => {
        return bindActionCreators(DepotBatchesActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(DepotBatchesActions, dispatch));
    }, [dispatch, setActions]);

    return [
        depotBatches,
        loadState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('depotBatches', reducer as any, logics as any);